// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file Adhesive coverage calculator implementation.
 * @author James Reid
 */

// @ts-check

// @@imports-package
import { Adhesive, Product } from "../products/index.js"
import { Stock, Bom } from "../stock/index.js"

// @@imports-utils
import { DoubleLinkedList, DoubleLinkedListNode } from "../utils/index.js"

// @@imports-types
import { AdhesiveType, Config } from "../types/index.js"

// @@body
/**
 * Adhesive coverage calculator implementation for tracking required adhesive
 * coverage for a given flat roof, and 
 * @todo Complete method documentation including inline comments, jsdoc
 * annotations, and class description.
 * @todo Check that all error throws use decorated error format.
 */
class AdhesiveCalculator {
    #stock
    #config
    #coverages = {
        waterBased: 0,
        contactBonding: 0,
        pu: 0,
        primer: 0,
        lapEdge: 0,
        waterBlock: 0,
        msPolymer: 0,
        blackSilicone: 0
    }

    /**
     * 
     * @param {Stock} stock 
     * @param {Config} config 
     */
    constructor(stock, config) {
        this.#stock = stock
        this.#config = config
    }

    /**
     * 
     * @param {AdhesiveType} type 
     * @param {number} coverage 
     */
    addAdhesive(type, coverage) {
        this.#coverages[type] += coverage
    }

    /**
     * 
     * @param {AdhesiveType} type 
     */
    #getClientCoverage(type) {
        const { adhesiveCoverages: { deck, sealants, primer } } = this.#config
        const clientCoverages = { ...deck, ...sealants, primer }

        return clientCoverages[type].client
    }

    /**
     * 
     * @param {AdhesiveType} type 
     * @param {number} volume 
     * @returns 
     */
    #getAdhesiveOption(type, volume) {
        volume *= this.#config.extraAllowances.adhesive
        const adhesives = this.#getAdhesiveLinkedList(type, volume)
        let adhesiveNode = /** @type {?DoubleLinkedListNode.<Adhesive>} */
            (adhesives.head)
        if (!adhesiveNode) { return }

        const options = [[{
            product: adhesiveNode.value,
            quantity: Math.ceil(volume / adhesiveNode.value.quantity)
        }]]

        while ((adhesiveNode = adhesiveNode.next)) {
            const option = [...options[0]]

            option[0] = {
                product: option[0].product,
                quantity: option[0].quantity > 0 ? option[0].quantity - 1 : 0
            }
            const optionVolume = option.reduce((acc, cur) => {
                return acc + cur.product.quantity * cur.quantity
            }, 0)

            const { value: lowProduct } = adhesiveNode
            const lowVolume = volume - optionVolume
            const lowQuantity = Math.ceil(lowVolume / lowProduct.quantity)

            option.unshift({ product: lowProduct, quantity: lowQuantity })
            options.unshift(option)
        }

        const lowerWasteVolume = volume +
            this.#config.clientLimits.adhesive.waste.lower
        options.sort((optionA, optionB) => {
            const {
                price: priceA,
                volume: volumeA,
                containerCount: containerCountA
            } = this.#getAdhesiveOptionDetails(optionA)
            const {
                price: priceB,
                volume: volumeB,
                containerCount: containerCountB
            } = this.#getAdhesiveOptionDetails(optionB)

            return volumeA < lowerWasteVolume && volumeB < lowerWasteVolume
                ? containerCountA - containerCountB || priceA - priceB
                : priceA - priceB || containerCountA - containerCountB
        })

        return options.at(0)?.filter(option => option.quantity > 0)
    }

    /**
     * 
     * @param {Array.<{product:Product, quantity:number}>} option
     * @returns {{price:number, volume:number, containerCount:number}}
     */
    #getAdhesiveOptionDetails(option) {
        return option.reduce((acc, cur) => {
            const { product: { price, quantity: volume }, quantity } = cur
            return {
                price: acc.price + price * quantity,
                volume: acc.volume + volume * quantity,
                containerCount: acc.containerCount + quantity
            }
        }, { price: 0, volume: 0, containerCount: 0 })
    }

    /**
     * 
     * @param {AdhesiveType} type 
     * @param {number} quantity
     * @returns 
     */
    #getAdhesiveLinkedList(type, quantity) {
        return new DoubleLinkedList(this.#stock
            .getAdhesives(type)
            .filter(adhesive => {
                return adhesive.stock * adhesive.quantity >= quantity
            })
            .sort((adhesiveA, adhesiveB) => {
                return adhesiveB.quantity - adhesiveA.quantity
            }))
    }

    get bom() {
        const bom = new Bom()

        const keys = /** @type {AdhesiveType[]} */
            (Object.keys(this.#coverages))
        for (const key of keys) {
            const volume = this.#coverages[key] / this.#getClientCoverage(key)
            if (!volume) { continue }
            const option = this.#getAdhesiveOption(key, volume)
            if (!option) {
                throw new Error()
            }
            for (const { product, quantity } of option) {
                bom.addLineItem(product, quantity)
            }
        }

        this.#stock.updateRunningStockQuantities(bom)

        return bom
    }
}

// @@exports
export { AdhesiveCalculator }
